// registry.mjs — the agent-rules catalog + PURE assembly helpers (FR, daily-auto-improve).
// 14 canonical software-engineering books (ciembor/agent-rules-books) distilled into
// agent-followable rule blocks. The Settings → Profile UI toggles which are active; the
// active set is injected into the auto-improve agent AND the Code-tab agent so "the scope
// always follows the best practices". Content comes from content.generated.mjs (bundled);
// this module owns the metadata + the budget-capped assembly. No I/O.

import { RULE_CONTENT } from './content.generated.mjs';

/** The catalog. `defaultOn` = the sensible core set enabled out of the box. */
export const RULE_BOOKS = Object.freeze([
  { slug: 'a-philosophy-of-software-design', title: 'A Philosophy of Software Design', author: 'Ousterhout', category: 'design', defaultOn: true, blurb: 'Deep modules, information hiding, reduce cognitive load.' },
  { slug: 'clean-code', title: 'Clean Code', author: 'Martin', category: 'craft', defaultOn: true, blurb: 'Readable, locally-reasonable code; small focused functions.' },
  { slug: 'the-pragmatic-programmer', title: 'The Pragmatic Programmer', author: 'Hunt & Thomas', category: 'craft', defaultOn: true, blurb: 'DRY, orthogonality, tracer bullets, pragmatic craft.' },
  { slug: 'refactoring', title: 'Refactoring', author: 'Fowler', category: 'refactoring', defaultOn: true, blurb: 'Behavior-preserving improvement in small safe steps.' },
  { slug: 'working-effectively-with-legacy-code', title: 'Working Effectively with Legacy Code', author: 'Feathers', category: 'refactoring', defaultOn: true, blurb: 'Seams + characterization tests before changing legacy code.' },
  { slug: 'clean-architecture', title: 'Clean Architecture', author: 'Martin', category: 'architecture', defaultOn: true, blurb: 'Dependency rule, boundaries, stable abstractions.' },
  { slug: 'release-it', title: 'Release It!', author: 'Nygard', category: 'reliability', defaultOn: true, blurb: 'Stability patterns: timeouts, bulkheads, circuit breakers.' },
  { slug: 'code-complete', title: 'Code Complete', author: 'McConnell', category: 'craft', defaultOn: false, blurb: 'Construction practices, defensive programming, routines.' },
  { slug: 'refactoring-guru', title: 'Refactoring.Guru', author: 'Shvets', category: 'refactoring', defaultOn: false, blurb: 'Code smells + design-pattern catalog.' },
  { slug: 'patterns-of-enterprise-application-architecture', title: 'Patterns of Enterprise Application Architecture', author: 'Fowler', category: 'architecture', defaultOn: false, blurb: 'Layering, domain logic, data source patterns.' },
  { slug: 'designing-data-intensive-applications', title: 'Designing Data-Intensive Applications', author: 'Kleppmann', category: 'data', defaultOn: false, blurb: 'Reliability, scalability, consistency of data systems.' },
  { slug: 'domain-driven-design', title: 'Domain-Driven Design', author: 'Evans', category: 'ddd', defaultOn: false, blurb: 'Ubiquitous language, aggregates, bounded contexts.' },
  { slug: 'domain-driven-design-distilled', title: 'Domain-Driven Design Distilled', author: 'Vernon', category: 'ddd', defaultOn: false, blurb: 'Condensed strategic + tactical DDD.' },
  { slug: 'implementing-domain-driven-design', title: 'Implementing Domain-Driven Design', author: 'Vernon', category: 'ddd', defaultOn: false, blurb: 'Applied DDD: aggregates, events, repositories.' },
]);

export const VARIANTS = Object.freeze(['nano', 'mini']);
export const DEFAULT_VARIANT = 'nano';
// Budget for the injected doctrine block (chars). nano ≈ 2KB/book ⇒ ~9 books fit;
// mini ≈ 6KB/book ⇒ ~3 books. Keeps the system-prompt addition bounded (~4.5k tokens).
export const DEFAULT_BUDGET_CHARS = 18000;

const BY_SLUG = new Map(RULE_BOOKS.map((b) => [b.slug, b]));

export function isValidSlug(slug) {
  return BY_SLUG.has(slug);
}

/** The catalog with each book's variant byte sizes (for the Settings UI). */
export function listBooks() {
  return RULE_BOOKS.map((b) => ({
    ...b,
    sizes: {
      nano: (RULE_CONTENT[b.slug]?.nano || '').length,
      mini: (RULE_CONTENT[b.slug]?.mini || '').length,
    },
  }));
}

/** Default-on slugs, in catalog order. */
export function defaultActiveSlugs() {
  return RULE_BOOKS.filter((b) => b.defaultOn).map((b) => b.slug);
}

/** Keep only valid slugs, deduped, in catalog order (stable + safe against junk input). */
export function normalizeActive(slugs) {
  const wanted = new Set(Array.isArray(slugs) ? slugs : []);
  return RULE_BOOKS.filter((b) => wanted.has(b.slug)).map((b) => b.slug);
}

export function normalizeVariant(variant) {
  return VARIANTS.includes(variant) ? variant : DEFAULT_VARIANT;
}

/** Resolve one book's text for a variant, falling back to the other variant if absent. */
export function ruleText(slug, variant) {
  const v = normalizeVariant(variant);
  const entry = RULE_CONTENT[slug];
  if (!entry) return '';
  return entry[v] || entry[v === 'nano' ? 'mini' : 'nano'] || '';
}

// FASE-12 E4-T04 — HEADER com PRECEDÊNCIA explícita. A doutrina guia o COMO (as
// decisões de código dentro do Modo Agente); a Constituição (Modo Agente/Chat) é a
// LEI e prevalece. Sem esta linha o modelo tratava doutrina e constituição como
// pares, e uma regra de livro podia parecer sobrepor um gate da LEI.
const DOCTRINE_HEADER = [
  '# CODING DOCTRINE (Apolo agent-rules)',
  '',
  'Follow these distilled rules from canonical software-engineering books. They constrain HOW you change code: prefer the option each rule favors; when a change conflicts with a rule, justify it or pick the rule-aligned path.',
  '',
  'PRECEDÊNCIA: esta doutrina orienta DECISÕES de código dentro do Modo Agente. A Constituição (Modo Agente de Código / Modo Chat) é a LEI e prevalece — ela define o QUÊ é permitido e os gates de conclusão; a doutrina só afina o COMO. Em conflito, a constituição vence.',
].join('\n');

const CATALOG_INDEX = new Map(RULE_BOOKS.map((b, i) => [b.slug, i]));
const CATEGORY_OF = new Map(RULE_BOOKS.map((b) => [b.slug, b.category]));

/**
 * Assemble the injectable doctrine block. PURE.
 *
 * FASE-12 E4-T02 — COTA POR CATEGORIA (anti drop-silencioso). O greedy-linear antigo
 * enchia por ORDEM DE CATÁLOGO até estourar o budget → sob budget apertado categorias
 * inteiras no fim do catálogo (reliability, ddd, data) sumiam enquanto craft levava 2-3
 * livros. Agora: Fase 1 reserva UM livro por categoria (fairness) antes de qualquer 2º;
 * Fase 2 preenche o resto do budget em ordem de catálogo. A SAÍDA sai em ordem de
 * catálogo (quando tudo cabe, `included == active` — ordem preservada, sem regressão).
 * Returns { block, included, dropped, chars }.
 * @param {{ activeSlugs?: string[], variant?: string, budgetChars?: number }} [opts]
 */
export function assembleRulesBlock({ activeSlugs, variant = DEFAULT_VARIANT, budgetChars = DEFAULT_BUDGET_CHARS } = {}) {
  const active = normalizeActive(activeSlugs);
  const v = normalizeVariant(variant);
  const cap = Number.isFinite(budgetChars) && budgetChars > 0 ? budgetChars : DEFAULT_BUDGET_CHARS;
  const additionFor = (slug) => `\n\n---\n\n${ruleText(slug, v)}`;

  // Candidatos = ativos COM texto, em ordem de catálogo. Sem texto → dropped.
  const noText = active.filter((slug) => !ruleText(slug, v));
  const candidates = active
    .filter((slug) => ruleText(slug, v))
    .sort((a, b) => (CATALOG_INDEX.get(a) ?? 0) - (CATALOG_INDEX.get(b) ?? 0));

  const includedSet = new Set();
  let used = DOCTRINE_HEADER.length;
  const tryAdd = (slug) => {
    if (includedSet.has(slug)) return false;
    const size = additionFor(slug).length;
    if (used + size > cap) return false;
    includedSet.add(slug); used += size; return true;
  };

  // Fase 1 — fairness: um livro por categoria (categorias na 1ª aparição no catálogo,
  // dentro da categoria em ordem de catálogo). Reserva a vaga antes de qualquer 2º livro.
  const byCat = new Map();
  for (const slug of candidates) {
    const c = CATEGORY_OF.get(slug) || 'other';
    if (!byCat.has(c)) byCat.set(c, []);
    byCat.get(c).push(slug);
  }
  for (const slugs of byCat.values()) {
    for (const slug of slugs) { if (tryAdd(slug)) break; }
  }
  // Fase 2 — preenche o budget restante com os demais, em ordem de catálogo.
  for (const slug of candidates) tryAdd(slug);

  const included = candidates.filter((s) => includedSet.has(s)); // ordem de catálogo
  const dropped = [...candidates.filter((s) => !includedSet.has(s)), ...noText];

  if (!included.length) return { block: '', included: [], dropped, chars: 0 };
  const block = DOCTRINE_HEADER + included.map(additionFor).join('');
  return { block, included, dropped, chars: block.length };
}
